import { create } from 'zustand';
import type { Drone, DroneGroup, Paginated } from '@shared/types';
import { api, errorMessage } from '../../lib/api';
import { invalidateDroneToken } from '../../drone';
import { syncLinkConfig } from '../../drone/store';

/**
 * 机队注册表缓存。
 *
 * 三个页签（总览 / 无人机管理 / 单机管理）共用同一份设备列表，
 * 因此把拉取与本地更新集中在这里，避免切页签时重复请求、或出现两份不一致的数据。
 */

const DRONE_PAGE_SIZE = 200;

interface GroupsResponse {
  items: DroneGroup[];
  usage: Record<string, number>;
}

interface FleetStore {
  drones: Drone[];
  groups: DroneGroup[];
  groupUsage: Record<string, number>;
  loading: boolean;
  loaded: boolean;
  error: string | null;
  load: (options?: { force?: boolean }) => Promise<void>;
  applyDrone: (drone: Drone) => void;
  applyDrones: (drones: Drone[]) => void;
  removeDrones: (ids: string[]) => void;
  setGroups: (groups: DroneGroup[], usage?: Record<string, number>) => void;
  upsertGroup: (group: DroneGroup) => void;
  removeGroup: (id: string) => void;
  /** 登出时清空，避免下一位用户看到上一位的数据 */
  reset: () => void;
}

export const useFleetStore = create<FleetStore>((set, get) => ({
  drones: [],
  groups: [],
  groupUsage: {},
  loading: false,
  loaded: false,
  error: null,

  async load(options) {
    if (get().loading) return;
    if (get().loaded && !options?.force) return;

    set({ loading: true, error: null });
    try {
      const [drones, groups] = await Promise.all([
        api.get<Paginated<Drone>>(`/api/drones?pageSize=${DRONE_PAGE_SIZE}`),
        api.get<GroupsResponse>('/api/groups'),
      ]);
      set({
        drones: drones.items,
        groups: groups.items,
        groupUsage: groups.usage ?? {},
        loading: false,
        loaded: true,
        error: null,
      });
      for (const drone of drones.items) syncLinkConfig(drone);
    } catch (error) {
      set({ loading: false, error: errorMessage(error) });
    }
  },

  applyDrone(drone) {
    const drones = get().drones;
    const index = drones.findIndex((item) => item.id === drone.id);
    const next = index === -1 ? [...drones, drone] : drones.map((item) => (item.id === drone.id ? drone : item));
    set({ drones: next });
    syncLinkConfig(drone);
  },

  applyDrones(updated) {
    const byId = new Map(updated.map((drone) => [drone.id, drone]));
    set({
      drones: get().drones.map((drone) => byId.get(drone.id) ?? drone),
    });
    for (const drone of updated) syncLinkConfig(drone);
  },

  removeDrones(ids) {
    const set0 = new Set(ids);
    set({ drones: get().drones.filter((drone) => !set0.has(drone.id)) });
    for (const id of ids) invalidateDroneToken(id);
  },

  setGroups(groups, usage) {
    set({ groups, groupUsage: usage ?? get().groupUsage });
  },

  upsertGroup(group) {
    const groups = get().groups;
    const index = groups.findIndex((item) => item.id === group.id);
    set({ groups: index === -1 ? [...groups, group] : groups.map((item) => (item.id === group.id ? group : item)) });
  },

  removeGroup(id) {
    set({ groups: get().groups.filter((group) => group.id !== id) });
  },

  reset() {
    set({
      drones: [],
      groups: [],
      groupUsage: {},
      loading: false,
      loaded: false,
      error: null,
    });
  },
}));

export function groupNameOf(groups: readonly DroneGroup[], groupId: string | null): string {
  if (!groupId) return '未分组';
  return groups.find((group) => group.id === groupId)?.name ?? '未分组';
}

export function groupColorOf(groups: readonly DroneGroup[], groupId: string | null): string | null {
  if (!groupId) return null;
  return groups.find((group) => group.id === groupId)?.color ?? null;
}
